"use client";

import { useQuery } from "@tanstack/react-query";
import type { Token } from "@/lib/api/transfers";
import { erc20BalanceOf, erc20BalanceOfVia } from "@/lib/chain-balance";
import { evmProvider } from "./detect";
import type { WalletChain } from "./types";

/* The connected wallet's balance of a deposit token, the figure the
   whitelabel's EvmDepositContent reads (useErc20TokenBalanceQuery) and shows
   under the address. Read on the chain the route is on, not the chain the
   wallet happens to be on: a wallet parked on Ethereum still has a real USDC
   balance on Base, and that is the one a Base deposit can spend.

   The read goes to the chain's public RPC first, as the bankroll tiles do.
   When no RPC is known for the chain — or it does not answer — the wallet
   itself is asked, which only works while it is on that chain. Either way a
   balance is read, never invented: `null` means unknown, and the panel says
   so rather than showing a zero. */

type Connection = {
  wallet: { id: string; chain: WalletChain };
  address: string;
  chainId?: number;
};

export const WALLET_BALANCE_REFETCH_MS = 10_000;

export const useWalletTokenBalance = (connection: Connection | null, token: Token, enabled: boolean) => {
  const chainId =
    token.network.environment === "evm"
      ? ((token.network.attributes as { chainId?: number } | undefined)?.chainId ?? undefined)
      : undefined;

  return useQuery({
    queryKey: ["wallet-balance", chainId ?? null, token.address, connection?.address ?? null],
    enabled: enabled && connection !== null && connection.wallet.chain === "evm" && chainId !== undefined,
    refetchInterval: WALLET_BALANCE_REFETCH_MS,
    staleTime: 5_000,
    queryFn: async ({ signal }): Promise<bigint | null> => {
      if (!connection || chainId === undefined) {
        return null;
      }

      const viaRpc = await erc20BalanceOf({
        chainId,
        tokenAddress: token.address,
        holder: connection.address,
        signal,
      });

      if (viaRpc !== null) {
        return viaRpc;
      }

      if (connection.chainId !== chainId) {
        return null;
      }

      return erc20BalanceOfVia(
        (args) => evmProvider(connection.wallet.id).request(args),
        token.address,
        connection.address,
      );
    },
  });
};
